import * as React from "react";
import { APIOutput, PageLibrary } from "@amzn/ask-legal-domain";
import { AppContext } from "../setup/context";

const globalPageLibraryCache: Record<string, PageLibrary> = {};
const pendingRequests = new Set<string>();

const subscribers = new Set<() => void>();
const notifySubscribers = () => {
    subscribers.forEach(callback => callback());
};

export const usePageLibraryDataCache = () => {
    const [, setUpdateCounter] = React.useState(0);
    const context = React.useContext(AppContext);

    React.useEffect(() => {
        const callback = () => setUpdateCounter(count => count + 1);
        subscribers.add(callback);
        return () => {
            subscribers.delete(callback);
        };
    }, []);

    const loadPageLibraryData = React.useCallback(async (pageLibraryIds: string[]) => {
        if (!pageLibraryIds?.length) return;
        const idsToLoad = pageLibraryIds.filter(id =>
            id && !globalPageLibraryCache[id] && !pendingRequests.has(id)
        );
        if (!idsToLoad.length) return;
        idsToLoad.forEach(id => pendingRequests.add(id));
        try {
            const loadPromises = idsToLoad.map(id => context.getPageLibraryAPI().load(id));
            const results = await Promise.all(loadPromises);
            let cacheUpdated = false;
            results.forEach((result, index) => {
                const id = idsToLoad[index];
                const output = APIOutput.fromRaw<PageLibrary>(result.data);
                if (output.isOk()) {
                    globalPageLibraryCache[id] = output.data;
                    cacheUpdated = true;
                }
                pendingRequests.delete(id);
            });
            if (cacheUpdated) {
                notifySubscribers();
            }
        } catch (error) {
            console.warn("Error loading page library data:", error);
            idsToLoad.forEach(id => pendingRequests.delete(id));
        }
    }, [context]);
    return { pageLibraryCache: globalPageLibraryCache, loadPageLibraryData };
};
